import type { HelpEntry } from "./helpCatalog";
import type { HelpScreen } from "./helpModel";

/** Paramètre d'adresse qui ouvre le centre d'aide sur une fiche : `/planning?aide=publier-la-semaine`. */
const HELP_LINK_PARAM = "aide";

const PENDING_KEY = "equiply.helpCenter.pendingLink";

/** Adresse de l'écran décrit par une fiche, section de « Paramètres » ou onglet de « Rapports » compris. */
export function screenHref(screen: HelpScreen): string {
  const params = new URLSearchParams();
  if (screen.section) params.set("section", screen.section);
  if (screen.tab) params.set("tab", screen.tab);
  const query = params.toString();
  return query ? `${screen.path}?${query}` : screen.path;
}

export function withHelpLinkParam(href: string, entryId: string): string {
  const [path, query = ""] = href.split("?");
  const params = new URLSearchParams(query);
  params.set(HELP_LINK_PARAM, entryId);
  return `${path}?${params.toString()}`;
}

/**
 * Lien à envoyer à un gérant (Crisp, SMS) : il ouvre l'écran de la fiche, puis la fiche.
 * Un ancien guide n'a pas d'écran fiable : son lien passe par « Suivi ».
 */
export function buildHelpLink(entry: HelpEntry, origin: string): string {
  const href = entry.kind === "fiche" ? screenHref(entry.fiche.screen) : "/dashboard";
  return `${origin}${withHelpLinkParam(href, entry.id)}`;
}

export function readHelpLinkParam(search: string): string | null {
  return new URLSearchParams(search).get(HELP_LINK_PARAM)?.trim() || null;
}

export function withoutHelpLinkParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(HELP_LINK_PARAM);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export interface PendingHelpLink {
  id: string;
  /** Page visée par le lien, sans le paramètre `aide`. */
  path: string;
}

/**
 * Au chargement de l'appli : garde le lien d'aide le temps de la connexion, qui
 * renvoie toujours vers « Suivi » et perdrait sinon l'adresse demandée.
 */
export function rememberPendingHelpLink(pathname: string, search: string): void {
  const id = readHelpLinkParam(search);
  if (!id) return;
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ id, path: pathname + withoutHelpLinkParam(search) }));
  } catch {
    // Stockage indisponible : le lien ne s'ouvrira que si la session est déjà active.
  }
}

export function takePendingHelpLink(): PendingHelpLink | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_KEY);
    const parsed = JSON.parse(raw) as Partial<PendingHelpLink>;
    return typeof parsed.id === "string" && typeof parsed.path === "string" ? { id: parsed.id, path: parsed.path } : null;
  } catch {
    return null;
  }
}
